import { useSubTour } from './SubTourContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Building2, 
  Truck, 
  Users, 
  Calendar, 
  DollarSign, 
  FileText, 
  Mountain,
  CheckCircle2,
  PlayCircle,
  Clock,
  ChevronRight
} from 'lucide-react';

// Icon mapping
const iconMap = {
  Building2,
  Truck,
  Users,
  Calendar,
  DollarSign,
  FileText,
  Mountain
};

const TrainingSection = () => {
  const {
    availableSubTours,
    startSubTour,
    isSubTourCompleted,
    getCompletionPercentage
  } = useSubTour();

  const completionPercentage = getCompletionPercentage();
  const completedCount = availableSubTours.filter(t => isSubTourCompleted(t.id)).length;

  return (
    <div className="space-y-6">
      {/* Progress Overview */}
      <Card className="bg-gradient-to-br from-[#ffd108]/10 to-transparent border-[#ffd108]/30">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-semibold text-lg">Training Progress</h3>
              <p className="text-sm text-muted-foreground">
                {completedCount} of {availableSubTours.length} guides completed
              </p>
            </div>
            <div className="text-3xl font-bold text-[#ffd108]">
              {completionPercentage}%
            </div>
          </div>
          <Progress value={completionPercentage} className="h-2" />
        </CardContent>
      </Card>

      {/* Sub-tour Cards */}
      <div className="grid gap-4">
        {availableSubTours.map((tour, index) => {
          const Icon = iconMap[tour.icon] || FileText;
          const isCompleted = isSubTourCompleted(tour.id);
          
          return (
            <Card 
              key={tour.id}
              className={`transition-all ${
                isCompleted 
                  ? 'bg-green-500/5 border-green-500/30' 
                  : 'hover:border-[#ffd108]/50 cursor-pointer'
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Step Number / Completion Icon */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCompleted 
                      ? 'bg-green-500/20' 
                      : 'bg-zinc-800'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <span className="text-sm font-semibold text-zinc-400">{index + 1}</span>
                    )}
                  </div>

                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isCompleted ? 'bg-green-500/10' : 'bg-[#ffd108]/10'
                  }`}>
                    <Icon className={`h-5 w-5 ${isCompleted ? 'text-green-500' : 'text-[#ffd108]'}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-white">{tour.title}</h4>
                      {isCompleted && (
                        <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30 text-xs">
                          Completed
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      {tour.description}
                    </p>
                  </div>

                  {/* Time & Action */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="flex items-center gap-1 text-xs text-zinc-500">
                      <Clock className="h-3 w-3" />
                      {tour.estimatedTime}
                    </div>
                    
                    <Button
                      size="sm"
                      onClick={() => startSubTour(tour.id)}
                      className={isCompleted 
                        ? 'bg-zinc-800 text-white hover:bg-zinc-700' 
                        : 'bg-[#ffd108] text-black hover:bg-[#ffd108]/90'
                      }
                    >
                      {isCompleted ? (
                        <>
                          <PlayCircle className="h-4 w-4 mr-1" />
                          Redo
                        </>
                      ) : (
                        <>
                          Start
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Completion Message */}
      {completionPercentage === 100 && (
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-6 text-center">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
            <h3 className="font-heading font-semibold text-lg text-white mb-2">
              All Training Complete!
            </h3>
            <p className="text-sm text-muted-foreground">
              You've mastered all the core workflows. You can redo any guide anytime to refresh your knowledge.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TrainingSection;
